import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchArrivalTasks,
  resetArrivalTasks,
  selectArrivalTasksData,
  selectArrivalTasksStatus,
  selectArrivalTasksError,
} from 'redux/slices/sla-reports/arrival-tasks';
import { getJavaScriptDate } from '_utils/functions';
import ReportHeader from './report-header';

const headerStyle = {
  border: '1px solid #e5e7eb',
  padding: '10px 14px',
  textAlign: 'left',
  backgroundColor: '#f9fafb',
  fontWeight: 600,
  fontSize: '13px',
  color: '#374151',
  whiteSpace: 'nowrap',
};

const cellStyle = {
  border: '1px solid #e5e7eb',
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: '13px',
  color: '#1f2937',
  whiteSpace: 'nowrap',
};

const nestedHeaderStyle = {
  ...headerStyle,
  backgroundColor: '#f1f5f9',
  fontSize: '11px',
  padding: '8px 12px',
};

const nestedCellStyle = {
  ...cellStyle,
  fontSize: '11px',
  padding: '8px 12px',
};

const tableStyle = {
  border: '1px solid #e5e7eb',
  borderCollapse: 'collapse',
  width: '100%',
  tableLayout: 'auto',
};

const TASK_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'type', label: 'Type' },
  { key: 'checkInTime', label: 'Check-in' },
  { key: 'allocatedTime', label: 'Allocated Time' },
  { key: 'allocatedBy', label: 'Allocated By' },
  { key: 'pickUp', label: 'Pick Up' },
  { key: 'dropOff', label: 'Drop Off' },
  { key: 'systemGate', label: 'System Gate' },
  { key: 'confirmTime', label: 'Confirm Time' },
  { key: 'atLocTime', label: 'At Loc Time' },
  { key: 'atLocVerifiedBy', label: 'At Loc Verified By' },
  { key: 'startTime', label: 'Start Time' },
  { key: 'completeTime', label: 'Complete Time' },
  { key: 'agent', label: 'Agent' },
  { key: 'comments', label: 'Comments' },
  { key: 'taskVehicle', label: 'Task Vehicle' },
  { key: 'status', label: 'Status' },
];

const FLIGHT_COLUMNS = [
  { key: 'flightNumber', label: 'Flight Number' },
  { key: 'origin', label: 'Origin' },
  { key: 'scheduledArrival', label: 'Scheduled Arrival' },
  { key: 'actualArrival', label: 'Actual Arrival' },
  { key: 'taskCount', label: 'Number of Tasks' },
  { key: 'isThirdState', label: 'Third State' },
];

const toLocalDateString = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const formatTime = (value) => {
  if (!value) return 'N/A';
  const date = getJavaScriptDate(value);
  if (!date || Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const TaskDetailsTable = ({ tasks }) => {
  if (!tasks?.length) {
    return (
      <div style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
        No ramp or marshall tasks assigned to this flight.
      </div>
    );
  }

  return (
    <table style={{ ...tableStyle, margin: '8px 0' }}>
      <thead>
        <tr>
          {TASK_COLUMNS.map((col) => (
            <th key={col.key} style={nestedHeaderStyle}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tasks.map((task, index) => (
          <tr key={task.id || index}>
            {TASK_COLUMNS.map((col) => {
              let value = task[col.key];
              if (['checkInTime', 'allocatedTime', 'confirmTime', 'atLocTime', 'startTime', 'completeTime'].includes(col.key)) {
                value = formatTime(value);
              } else if (col.key === 'taskVehicle') {
                value = value || 'No Vehicle';
              } else {
                value = displayValue(value);
              }
              return (
                <td key={col.key} style={nestedCellStyle}>{value}</td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ArrivalTasksReport = ({ dateRange, iata, runReportRef, onRunLoadingChange }) => {
  const dispatch = useDispatch();
  const data = useSelector(selectArrivalTasksData);
  const status = useSelector(selectArrivalTasksStatus);
  const error = useSelector(selectArrivalTasksError);
  const isLoading = status === 'loading';

  const [thirdStateOnly, setThirdStateOnly] = useState(false);
  const [expandedFlightId, setExpandedFlightId] = useState(null);

  const handleRunReport = useCallback(() => {
    const startDate = dateRange?.[0];
    const endDate = dateRange?.[1];

    if (!startDate || !endDate || !iata) return;
    if (isLoading) return;

    dispatch(fetchArrivalTasks({
      startDate: toLocalDateString(startDate),
      endDate: toLocalDateString(endDate),
      iata,
    }));
  }, [dispatch, dateRange, iata, isLoading]);

  useEffect(() => {
    return () => {
      dispatch(resetArrivalTasks());
    };
  }, [dispatch]);

  useEffect(() => {
    if (runReportRef) runReportRef.current = handleRunReport;
    return () => { if (runReportRef) runReportRef.current = null; };
  }, [handleRunReport, runReportRef]);

  useEffect(() => {
    if (onRunLoadingChange) onRunLoadingChange(isLoading);
  }, [isLoading, onRunLoadingChange]);

  useEffect(() => {
    setExpandedFlightId(null);
  }, [data, thirdStateOnly]);

  const filteredFlights = useMemo(() => {
    const flights = data?.flights ?? [];
    if (!thirdStateOnly) return flights;
    return flights.filter((flight) => flight.isThirdState);
  }, [data, thirdStateOnly]);

  const toggleExpanded = (flightId) => {
    setExpandedFlightId((prev) => (prev === flightId ? null : flightId));
  };

  return (
    <div>
      <ReportHeader
        title="Arrival Tasks Report"
        description="Arrival flights with assigned ramp and marshall tasks"
        onRunReport={handleRunReport}
        isLoading={isLoading}
        hideRunButton={!!runReportRef}
        checkboxes={[
          {
            label: 'Third State Only',
            checked: thirdStateOnly,
            onChange: (e) => setThirdStateOnly(e.target.checked),
          },
        ]}
      />

      {status === 'failed' && (
        <p style={{ color: '#991b1b', fontSize: '13px' }}>
          Error loading Arrival Tasks report: {error}
        </p>
      )}

      {data && (
        <>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
            Showing {filteredFlights.length} arrival flight{filteredFlights.length === 1 ? '' : 's'}
            {thirdStateOnly ? ' (third state only)' : ''}
          </p>

          {filteredFlights.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}>
              No flights match the selected filters.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...headerStyle, width: '40px' }} />
                    {FLIGHT_COLUMNS.map((col) => (
                      <th key={col.key} style={headerStyle}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredFlights.map((flight) => {
                    const isExpanded = expandedFlightId === flight.flightId;
                    return (
                      <React.Fragment key={flight.flightId || `${flight.flightNumber}-${flight.flightDate}`}>
                        <tr
                          onClick={() => toggleExpanded(flight.flightId)}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isExpanded ? '#eff6ff' : 'transparent',
                          }}
                        >
                          <td style={{ ...cellStyle, textAlign: 'center', color: '#3b82f6', fontWeight: 600 }}>
                            {isExpanded ? '−' : '+'}
                          </td>
                          <td style={cellStyle}>{displayValue(flight.flightNumber)}</td>
                          <td style={cellStyle}>{displayValue(flight.origin)}</td>
                          <td style={cellStyle}>{formatTime(flight.scheduledArrival)}</td>
                          <td style={cellStyle}>{formatTime(flight.actualArrival)}</td>
                          <td style={cellStyle}>{flight.taskCount ?? 0}</td>
                          <td style={cellStyle}>{flight.isThirdState ? 'Y' : 'N'}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={FLIGHT_COLUMNS.length + 1} style={{ padding: '12px 16px', backgroundColor: '#f8fafc' }}>
                              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                                Task Information
                              </p>
                              <TaskDetailsTable tasks={flight.tasks} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArrivalTasksReport;
